/* Cover specs: turns long tonneau-cover titles into scannable chips
   (bed size, RamBox / tailgate / deck-rail options, finish, rail type)
   and, when a vehicle-filtered list shows a handful of covers, tells the
   shopper exactly what the options differ by.

   Works on: Convermax result cards (.cm_product-item), Lakewood product
   cards (.lw-product-card) and the product page title (.lw-pdp__title).
   Never rewrites Convermax's own elements - it only adds a block after the
   title and a data attribute on the card, so their re-renders stay safe. */
(function () {
  'use strict';

  var COVER_RE = /tonneau|bed cover|bakflip|revolver|retrax|powertrax|lomax|vanish|literider|tonnosport|lorado|sentry|truxport|deuce|lo pro|pro x15|roll-up cover|tri-fold|eq retractable/i;
  /* Other bed-size-specific parts that get the same chips (no description lookups for these). */
  var SPEC_RE = /bed mat|bed liner|bedliner|bed rack|utility rails|truck topper|tailgate mat|drawer system|cargoglide|impactliner/i;

  function clean(s) { return String(s || '').replace(/\s+/g, ' ').trim(); }
  function cap(s) { s = clean(s); return s.charAt(0).toUpperCase() + s.slice(1); }
  function esc(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var BED_RES = [
    /(\d{1,2}\.\d)\s*ft\.?\s*(?:long\s+)?(?:bed|box)\b/i,
    /(\d{1,2})\s*'\s*(\d{1,2}(?:\.\d+)?)?\s*(?:"|”|'')?\s*(?:long\s+)?(?:bed|box)\b/i,
    /(\d{1,2})\s*ft\.?\s*(?:(\d{1,2}(?:\.\d+)?)\s*in\.?)?\s*(?:bed|box)\b/i
  ];

  function formatBed(ft, inch) {
    var i = inch ? parseFloat(inch) : 0;
    if (!i) return ft + "'";
    return ft + "' " + (Math.round(i * 10) / 10) + '"';
  }

  /* Split "without RamBox with or without Multifunction Tailgate" into phrases. */
  function splitQualifiers(text) {
    text = clean(text
      .replace(/\bw\/\s*out\b/gi, 'without')
      .replace(/\bw\/o\b/gi, 'without')
      .replace(/\bw\/\s*/gi, 'with ')
      .replace(/\bwith or without\b/gi, 'WITHORWITHOUT'));
    if (!text) return [];
    var parts = [];
    text.split(/\s+-\s+|;|,/).forEach(function (seg) {
      seg = clean(seg.replace(/^[-\s]+|[-\s]+$/g, ''));
      if (!seg) return;
      seg.split(/\s+(?=(?:WITHORWITHOUT|without|with|includes|including)\b)/i).forEach(function (p) {
        p = clean(p.replace(/WITHORWITHOUT/g, 'with or without').replace(/^\(|\)$/g, ''));
        if (p) parts.push(p);
      });
    });
    return parts;
  }

  /* One qualifier phrase -> {topic, label, kind}. topic lets us compare
     "with RamBox" vs "without RamBox" across cards. */
  function qualifier(p) {
    var m;
    if ((m = p.match(/^with or without\s+(.+)$/i))) return { topic: m[1].toLowerCase(), name: clean(m[1]), label: 'With or without ' + clean(m[1]), kind: 'either' };
    if ((m = p.match(/^without\s+(.+)$/i))) return { topic: m[1].toLowerCase(), name: clean(m[1]), label: 'No ' + m[1], kind: 'without' };
    if ((m = p.match(/^with\s+(.+)$/i))) return { topic: m[1].toLowerCase(), name: clean(m[1]), label: 'With ' + m[1], kind: 'with' };
    if ((m = p.match(/^includ(?:es|ing)\s+(.+)$/i))) return { topic: m[1].toLowerCase(), name: clean(m[1]), label: 'Fits ' + m[1], kind: 'with' };
    return { topic: p.toLowerCase(), label: cap(p), kind: 'note' };
  }

  var SKIP_FEATURE = /^(tonneau cover|hard folding tonneau cover|retractable tonneau cover|roll-up cover)$/i;

  function parseTitle(title) {
    title = clean(title);
    var spec = { line: '', vehicle: '', bed: '', features: [], quals: [] };
    var y = title.search(/\b(?:19|20)\d{2}\b/);
    var head = y > -1 ? title.slice(0, y) : title;
    var fit = y > -1 ? title.slice(y) : '';

    var segs = head.split(/\s+-\s+/).map(function (s) { return clean(s.replace(/[-\s]+$/, '')); }).filter(Boolean);
    spec.line = (segs.shift() || title).replace(/^RealTruck\s+/i, '');
    var seen = {};
    segs.forEach(function (s) {
      var k = s.toLowerCase();
      if (SKIP_FEATURE.test(s) || seen[k] || /^[A-Z0-9]{4,}$/.test(s)) return;
      seen[k] = true;
      spec.features.push(s);
    });

    if (!fit) return spec;

    var bm = null, bi = -1;
    for (var i = 0; i < BED_RES.length && !bm; i++) { bm = fit.match(BED_RES[i]); bi = i; }
    var tail = '';
    if (bm) {
      var beds = [bi === 0 ? bm[1] + ' ft' : formatBed(bm[1], bm[2])];
      var before = fit.slice(0, bm.index);
      /* "5' 7\" / 6' 4\" Bed" - one cover that fits more than one bed length */
      var multi = before.match(/((?:\d{1,2}\s*'\s*(?:\d{1,2}(?:\.\d+)?\s*(?:"|”))?\s*(?:\/|or)\s*)+)$/i);
      if (multi) {
        var extra = [];
        multi[1].replace(/(\d{1,2})\s*'\s*(?:(\d{1,2}(?:\.\d+)?)\s*(?:"|”))?/g, function (_, f, i) { extra.push(formatBed(f, i)); return _; });
        beds = extra.concat(beds);
        before = before.slice(0, before.length - multi[1].length);
      }
      spec.bed = beds.join(' / ');
      spec.vehicle = clean(before.replace(/[-\s]+$/, ''));
      tail = fit.slice(bm.index + bm[0].length);
    } else {
      /* No bed length in the title (e.g. Ford Maverick): trailing " - " parts are options. */
      var fs = fit.split(/\s+-\s+/);
      spec.vehicle = clean(fs.shift());
      tail = fs.join(' - ');
    }
    spec.vehicle = spec.vehicle.replace(/\s*-\s*$/, '');
    spec.quals = splitQualifiers(tail).map(qualifier);
    return spec;
  }

  /* ACCESS (ACI) titles are "Name - SKU"; finish and rail live in the description. */
  function specFromDescription(html) {
    var text = clean(String(html || '').replace(/<[^>]+>/g, ' '));
    var out = [];
    var finish = text.match(/\b(Black Diamond Mist|Black Urethane|Carbon Fiber|Textured Matte Black|Matte Black|Black Moto|Black Matte)\b/i);
    if (finish) out.push({ topic: 'finish', label: cap(finish[1]) + ' finish', kind: 'note' });
    var rail = text.match(/\b(Single|Split)\s+Rail\b/i);
    if (rail) out.push({ topic: 'rail', label: cap(rail[1]) + ' rail', kind: 'note' });
    if (/Works w\/Toolbox:\s*Yes/i.test(text)) out.push({ topic: 'toolbox', label: 'Toolbox compatible', kind: 'with' });
    return out;
  }

  function chipsHtml(spec) {
    var h = '';
    if (spec.bed) h += '<span class="lw-spec-chip lw-spec-chip--bed" data-topic="bed" data-val="' + esc(spec.bed) + '"><small>Bed</small>' + esc(spec.bed) + '</span>';
    spec.features.forEach(function (f) {
      h += '<span class="lw-spec-chip lw-spec-chip--feature" data-topic="feature:' + esc(f.toLowerCase()) + '" data-val="1">' + esc(f) + '</span>';
    });
    spec.quals.forEach(function (q) {
      h += '<span class="lw-spec-chip lw-spec-chip--' + q.kind + '" data-topic="' + esc(q.topic) + '" data-name="' + esc(q.name || '') + '" data-val="' + esc(q.kind === 'note' ? q.label : q.kind) + '">' + esc(q.label) + '</span>';
    });
    return h;
  }

  function blockHtml(spec, opts) {
    var chips = chipsHtml(spec);
    var fits = spec.vehicle ? '<div class="lw-spec__fits"><span>Fits</span> ' + esc(spec.vehicle) + '</div>' : '';
    if (!chips && !fits) return '';
    return (opts && opts.showLine && spec.line ? '<div class="lw-spec__line">' + esc(spec.line) + '</div>' : '') +
      fits + (chips ? '<div class="lw-spec__chips">' + chips + '</div>' : '');
  }

  /* ---------- product JSON cache (for ACI descriptions) ---------- */
  var jsonCache = {};
  function productJson(handle) {
    if (!jsonCache[handle]) {
      var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      jsonCache[handle] = fetch(base + 'products/' + handle + '.js')
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .catch(function () { return null; });
    }
    return jsonCache[handle];
  }
  function handleFromHref(href) {
    var m = String(href || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  /* ---------- cards ---------- */
  var CARD_SOURCES = [
    { card: '.cm_product-item', title: '.cm_product-item__title', kind: 'cm' },
    { card: '.lw-product-card:not(.lw-line-card):not(.lw-fit-group)', title: '.lw-product-title', kind: 'lw' }
  ];

  function renderCard(card, src) {
    var titleEl = card.querySelector(src.title);
    if (!titleEl) return;
    var title = clean(titleEl.textContent);
    if (card.getAttribute('data-lw-spec-title') === title) return;

    var old = card.querySelector(':scope .lw-spec');
    if (old) old.remove();
    card.removeAttribute('data-lw-spec');
    card.setAttribute('data-lw-spec-title', title);
    var isCover = COVER_RE.test(title);
    if (!isCover && !SPEC_RE.test(title)) return;

    var spec = parseTitle(title);
    var block = document.createElement('div');
    block.className = 'lw-spec lw-spec--card';
    block.setAttribute('data-lw-spec-block', '');

    function place(html) {
      if (!html) return;
      block.innerHTML = html;
      if (!block.isConnected) titleEl.insertAdjacentElement('afterend', block);
      card.setAttribute('data-lw-spec', spec.bed ? 'bed' : 'basic');
      if (!titleEl.getAttribute('title')) titleEl.setAttribute('title', title);
      scheduleCompare();
    }

    if (spec.vehicle || spec.bed || spec.quals.length) {
      place(blockHtml(spec));
      return;
    }
    /* Title has no fitment (ACCESS covers): pull finish/rail from the description. */
    if (!isCover) return;
    var link = card.querySelector('a[href*="/products/"]');
    var handle = handleFromHref(link && link.getAttribute('href'));
    if (!handle) return;
    productJson(handle).then(function (p) {
      if (!p || card.getAttribute('data-lw-spec-title') !== title) return;
      spec.quals = specFromDescription(p.description);
      place(blockHtml(spec));
    });
  }

  /* ---------- "what's the difference?" helper ---------- */
  var compareTimer = null;
  function scheduleCompare() {
    clearTimeout(compareTimer);
    compareTimer = setTimeout(compareAll, 120);
  }

  var TOPIC_NAMES = { bed: 'bed length', finish: 'finish', rail: 'rail type', toolbox: 'toolbox fit' };
  function topicName(t) {
    if (TOPIC_NAMES[t]) return TOPIC_NAMES[t];
    if (t.indexOf('feature:') === 0) return t.slice(8);
    return t;
  }

  function compareAll() {
    document.querySelectorAll('.cmRepeater_items, .lw-collection-grid:not(.lw-lg-grid):not(.lw-fit__grid)').forEach(compareGroup);
  }

  function compareGroup(grid) {
    var cards = Array.prototype.filter.call(grid.children, function (c) {
      return c.querySelector && c.querySelector('[data-lw-spec-block]') && c.offsetParent !== null;
    });
    grid.querySelectorAll('.lw-spec-chip.is-diff').forEach(function (c) { c.classList.remove('is-diff'); });
    var host = grid.closest('#cm-collection-page, .lw-collection-products, .lw-collection-shop') || grid.parentElement;
    var banner = host && host.parentElement ? host.parentElement.querySelector(':scope > .lw-spec-diff') : null;

    if (cards.length < 2 || cards.length > 12) { if (banner) banner.hidden = true; return; }

    /* topic -> set of values across the visible cards */
    var topics = {};
    var displayNames = {};
    cards.forEach(function (card, idx) {
      card.querySelectorAll('.lw-spec-chip').forEach(function (chip) {
        var t = chip.getAttribute('data-topic');
        if (chip.getAttribute('data-name')) displayNames[t] = chip.getAttribute('data-name');
        (topics[t] = topics[t] || { vals: {}, cards: {} });
        topics[t].vals[chip.getAttribute('data-val')] = true;
        topics[t].cards[idx] = true;
      });
    });
    var diff = Object.keys(topics).filter(function (t) {
      var info = topics[t];
      var missing = Object.keys(info.cards).length < cards.length;
      return missing || Object.keys(info.vals).length > 1;
    });
    if (!diff.length) { if (banner) banner.hidden = true; return; }

    diff.forEach(function (t) {
      grid.querySelectorAll('.lw-spec-chip[data-topic="' + (window.CSS && CSS.escape ? CSS.escape(t) : t) + '"]').forEach(function (c) {
        c.classList.add('is-diff');
      });
    });

    if (!host || !host.parentElement) return;
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'lw-spec-diff';
      host.parentElement.insertBefore(banner, host);
    }
    var names = diff.map(function (t) { return displayNames[t] || topicName(t); }).filter(function (v, i, a) { return a.indexOf(v) === i; });
    var hasBed = diff.indexOf('bed') > -1;
    banner.innerHTML =
      '<div class="lw-spec-diff__icon" aria-hidden="true">?</div>' +
      '<div><strong>' + cards.length + ' covers match &mdash; here&rsquo;s how they differ</strong>' +
      '<p>Pick the one that matches your truck: <b>' + esc(names.join(', ')) + '</b>. The highlighted tags on each cover show the difference.</p>' +
      (hasBed ? '<p class="lw-spec-diff__tip">Not sure of your bed length? Measure inside the bed from the front wall (bulkhead) to the closed tailgate.</p>' : '') +
      '</div>';
    banner.hidden = false;
  }

  function scan(root) {
    CARD_SOURCES.forEach(function (src) {
      (root || document).querySelectorAll(src.card).forEach(function (card) { renderCard(card, src); });
    });
  }

  /* ---------- product page ---------- */
  function renderPdp() {
    var h1 = document.querySelector('.lw-pdp__title');
    if (!h1 || h1.getAttribute('data-lw-spec-done')) return;
    h1.setAttribute('data-lw-spec-done', '1');
    var title = clean(h1.textContent);
    if (!COVER_RE.test(title) && !SPEC_RE.test(title)) return;
    var spec = parseTitle(title);
    if (!spec.bed && !spec.vehicle && !spec.quals.length) {
      var desc = document.querySelector('.lw-pdp__accordion-content.rte');
      spec.quals = specFromDescription(desc ? desc.innerHTML : '');
    }
    var html = blockHtml(spec);
    if (!html) return;
    var box = document.createElement('div');
    box.className = 'lw-spec lw-spec--pdp';
    box.innerHTML = '<div class="lw-spec__label">' + (spec.vehicle ? 'Made for this truck &amp; bed' : 'Cover options') + '</div>' + html;
    h1.insertAdjacentElement('afterend', box);
  }

  /* ---------- boot ---------- */
  var pending = false;
  function onMutate() {
    if (pending) return;
    pending = true;
    setTimeout(function () { pending = false; scan(document); }, 60);
  }

  function boot() {
    renderPdp();
    scan(document);
    var target = document.getElementById('MainContent') || document.body;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        if (t && t.closest && t.closest('[data-lw-spec-block], .lw-spec-diff')) continue;
        onMutate();
        return;
      }
    }).observe(target, { childList: true, subtree: true, characterData: true });
    window.addEventListener('resize', scheduleCompare);
  }

  window.LakewoodCoverSpecs = {
    parseTitle: parseTitle,
    specFromDescription: specFromDescription,
    refresh: function () { scan(document); scheduleCompare(); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
